import { IE } from '~/settings/__modules__/utils/BrowserService';

export const IndexDBStatus = {
  Exist: 1,
  NotExists: 0,
  NotSupport: -1,
};

const checkIfHasIndexDB = () => {
  let hasIndexedDB = 'indexedDB' in window && indexedDB;

  if (navigator.appVersion.indexOf('Edge/18.17763') > 0) {
    //for edge 17763 bugs
    hasIndexedDB = null;
  } else {
    let isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    if (isSafari) {
      //detect private mode for safari
      let storage = window.sessionStorage;
      try {
        storage.setItem('someKeyHere', 'test');
        storage.removeItem('someKeyHere');
      } catch (e) {
        if (
          e.code === DOMException.QUOTA_EXCEEDED_ERR &&
          storage.length === 0
        ) {
          hasIndexedDB = null;
        }
      }
    }
  }
  return hasIndexedDB;
};

let hasIndexDB = checkIfHasIndexDB();

class IndexDBService {
  dbName = null;
  isSupport = false;
  DB;
  DB_Ready;

  constructor(forDBName) {
    this.dbName = forDBName;
  }

  async support() {
    if (hasIndexDB) {
      const result = await this.exists();
      if (result === IndexDBStatus.NotSupport) {
        this.isSupport = false;
      } else {
        this.isSupport = true;
      }
    } else {
      this.isSupport = false;
    }
    return this.isSupport;
  }

  static checkIfHasIndexDB() {
    return checkIfHasIndexDB();
  }

  async checkSupportAndExist() {
    let result = false;
    if (hasIndexDB) {
      result = await this.exists();
      if (result === IndexDBStatus.NotSupport) {
        this.isSupport = false;
      } else {
        this.isSupport = true;
      }
    } else {
      this.isSupport = false;
    }
    return { support: this.isSupport, result };
  }

  exists() {
    const dbName = this.dbName;
    return new Promise((resolve) => {
      let timer = null;
      const checkLocalStorage = () => {
        const supportStampSuggestion =
          localStorage.getItem('supportStampSuggestion') === 'true';
        const stampSuggestionExist =
          localStorage.getItem('StampSuggestionExist') === 'true';
        if (!supportStampSuggestion) {
          resolve(IndexDBStatus.NotSupport);
        } else if (stampSuggestionExist) {
          resolve(IndexDBStatus.Exist);
        } else {
          resolve(IndexDBStatus.NotExists);
        }
      };
      let record = [];
      if (IE) {
        timer = setTimeout(checkLocalStorage, 300);
      }
      try {
        let DBOpenRequest = indexedDB.open(dbName);

        DBOpenRequest.onerror = () => {
          if (record.length === 0) {
            resolve(IndexDBStatus.NotSupport);
          }
        };

        DBOpenRequest.onsuccess = (event) => {
          record.push('onsuccess');
          this.DB = event.target.result;
          this.DB_Ready = true;
          resolve(IndexDBStatus.Exist);
        };

        DBOpenRequest.onupgradeneeded = function (e) {
          record.push('');
          if (!(e && e.target)) {
            console.error(
              'Unable to capture the e.target in DBOpenRequest.onupgradeneeded',
            );
          } else {
            e.target.transaction.abort();
          }
          if (IE) {
            timer && clearTimeout(timer);
            const supportStampSuggestion = localStorage.getItem(
              'supportStampSuggestion',
            );
            const stampSuggestionExist = localStorage.getItem(
              'StampSuggestionExist',
            );
            if (
              supportStampSuggestion === null &&
              stampSuggestionExist === null
            ) {
              resolve(IndexDBStatus.NotExists);
            } else {
              checkLocalStorage();
            }
          } else {
            resolve(IndexDBStatus.NotExists);
          }
        };
      } catch (e) {
        this.isSupport = false;
        resolve(IndexDBStatus.NotSupport);
      }
    });
  }

  create(tables) {
    return new Promise((resolve) => {
      let DBOpenRequest = indexedDB.open(this.dbName);
      DBOpenRequest.onsuccess = (event) => {
        this.DB = event.target.result;
        this.DB_Ready = true;
      };

      DBOpenRequest.onerror = function (event) {
        console.error && console.error('Failed to create DB', event);
      };

      DBOpenRequest.onupgradeneeded = (event) => {
        this.DB = event.target.result;
        console.log('DBOpenRequest.onupgradeneeded', event);

        let createSchemaTran = event.target.transaction;

        const tableNames = tables.map((x) => x.name);
        tableNames.forEach((tableName, i) => {
          this.DB.createObjectStore(tableName, { keyPath: tables[i].keyPath });
        });
        createSchemaTran.oncomplete = function () {
          resolve(true);
        };
      };
    });
  }

  remove() {
    if (this.DB) {
      this.DB.close();
    }

    return new Promise((resolve, reject) => {
      let deleteRequest = indexedDB.deleteDatabase(this.dbName);

      deleteRequest.onsuccess = function () {
        resolve(true);
      };

      deleteRequest.onerror = function () {
        reject(false);
      };

      deleteRequest.onblocked = function () {
        reject(false);
      };

      deleteRequest.onupgradeneeded = function () {
        reject(false);
      };
    });
  }

  insert(tableName, items) {
    this.insertOrUpdate(tableName, items);
  }

  update(tableName, items) {
    this.insertOrUpdate(tableName, items, true);
  }

  insertAsBatch(tableName, items) {
    let tran = this.DB.transaction([tableName], 'readwrite');

    let targetTable = tran.objectStore(tableName);

    return new Promise((resolve, reject) => {
      tran.oncomplete = function () {
        resolve(true);
      };

      tran.onerror = function (event) {
        console.error('indexdb.insertItems.error', targetTable, event);
        reject(false);
      };

      items.forEach((x) => targetTable.add(x));
    });
  }

  insertOrUpdate(tableName, item, update = false) {
    let tran = this.DB.transaction([tableName], 'readwrite');

    let targetTable = tran.objectStore(tableName);

    return new Promise((resolve, reject) => {
      tran.oncomplete = function () {
        resolve(true);
      };

      tran.onerror = function (event) {
        console.error('indexdb.insertItems.error', targetTable, event);
        reject(false);
      };

      if (update) {
        targetTable.put(item);
      } else {
        targetTable.add(item);
      }
    });
  }

  query(tableName, keyQuery) {
    return new Promise((resovle) => {
      let tran = this.DB.transaction([tableName]);
      let request = tran.objectStore(tableName).get(keyQuery);

      request.onerror = function (event) {
        resovle([event || new Error('DBQUERY'), null]);
      };

      request.onsuccess = function () {
        if (request.result) {
          resovle([null, request.result]);
        } else {
          resovle([null, null]);
        }
      };
    });
  }
}

export { IndexDBService, checkIfHasIndexDB };
